import type { BeadColor } from './types';

/**
 * Tri visuel des couleurs de perles selon une logique de roue chromatique,
 * pour rendre toute la palette « lisible d'un coup d'œil » : couleurs proches
 * regroupées, neutres séparés.
 *
 * **Important** : cet utilitaire **ne réordonne pas le catalogue canonique**.
 * L'ordre originel de `HAMA_MIDI_COLORS` est figé (les `pattern.cells[]`
 * stockent des index dans cet ordre — les bouger casserait les patrons
 * existants). Cette fonction sert uniquement à produire un **ordre
 * d'affichage** dans l'UI (grille palette, guide des perles, etc.).
 *
 * Algorithme :
 *  1. Conversion sRGB → HSL.
 *  2. Si saturation < seuil OU lightness extrême → bucket « neutre »,
 *     trié du plus clair (blanc) au plus sombre (noir).
 *  3. Sinon → bucket « chromatique », trié par teinte H (0-360, soit
 *     rouge → orange → jaune → vert → cyan → bleu → magenta), avec
 *     fallback lightness desc (clair → foncé) pour les couleurs de même
 *     teinte.
 *  4. Les neutres viennent **avant** les chromatiques (les blancs/crèmes
 *     en tête, les noirs/bruns dilués en fin de neutres).
 *
 * NB : « Crème » a une saturation modérée et tombera dans les chromatiques
 * (zone orange-jaune). Cohérent visuellement avec une roue.
 */

const NEUTRAL_SAT_THRESHOLD = 0.12;
const LIGHT_EXTREME_HI = 0.97;
const LIGHT_EXTREME_LO = 0.03;

type Hsl = { h: number; s: number; l: number };

function rgbToHsl(r: number, g: number, b: number): Hsl {
  const rn = r / 255;
  const gn = g / 255;
  const bn = b / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  const l = (max + min) / 2;
  if (max === min) return { h: 0, s: 0, l };
  const d = max - min;
  const s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
  let h: number;
  if (max === rn) h = ((gn - bn) / d + (gn < bn ? 6 : 0)) / 6;
  else if (max === gn) h = ((bn - rn) / d + 2) / 6;
  else h = ((rn - gn) / d + 4) / 6;
  return { h, s, l };
}

type SortKey = {
  /** 0 = neutre (vient avant), 1 = chromatique. */
  bucket: 0 | 1;
  /** Clé principale dans le bucket (lightness desc pour neutres, hue asc pour chromatiques). */
  primary: number;
  /** Clé secondaire pour départager (lightness desc dans chromatiques). */
  secondary: number;
};

function sortKey(color: BeadColor): SortKey {
  const { r, g, b } = color.rgb;
  const { h, s, l } = rgbToHsl(r, g, b);
  if (s < NEUTRAL_SAT_THRESHOLD || l > LIGHT_EXTREME_HI || l < LIGHT_EXTREME_LO) {
    // Neutres : du plus clair au plus sombre. `-l` → blanc (l=1) en premier.
    return { bucket: 0, primary: -l, secondary: 0 };
  }
  // Chromatiques : par teinte croissante (0=rouge, 0.33=vert, 0.67=bleu),
  // puis par lightness descendante pour grouper les mêmes teintes du clair
  // au foncé.
  return { bucket: 1, primary: h, secondary: -l };
}

/**
 * Retourne un nouveau tableau de couleurs trié selon la logique visuelle.
 * L'input n'est pas muté.
 */
export function sortColorsByHue(colors: ReadonlyArray<BeadColor>): BeadColor[] {
  // Tri stable : on conserve l'ordre catalogue à clé strictement égale,
  // ce qui rend l'ordre déterministe et reproductible pour les tests.
  return colors
    .map((c, originalIdx) => ({ c, k: sortKey(c), originalIdx }))
    .sort((a, b) => {
      if (a.k.bucket !== b.k.bucket) return a.k.bucket - b.k.bucket;
      if (a.k.primary !== b.k.primary) return a.k.primary - b.k.primary;
      if (a.k.secondary !== b.k.secondary) return a.k.secondary - b.k.secondary;
      return a.originalIdx - b.originalIdx;
    })
    .map((x) => x.c);
}

/**
 * Construit le mapping inverse `id → index canonique dans la liste source`.
 * Permet à l'UI d'itérer dans l'ordre trié tout en conservant les index
 * canoniques (utilisés par `pattern.cells[]` et `activeColorIdx`).
 */
export function canonicalIndexById(
  colors: ReadonlyArray<BeadColor>,
): ReadonlyMap<string, number> {
  const m = new Map<string, number>();
  for (let i = 0; i < colors.length; i++) {
    const c = colors[i];
    if (c) m.set(c.id, i);
  }
  return m;
}
